// src/components/layout/SidebarMenu.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart,
  Users, 
  BarChart2,
  Settings,
  MessageCircle,
  MessageSquare,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  BarChart // Добавляем импорт иконки для маркетинговых расходов
} from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from '@/styles/layout/SidebarMenu.module.css';

// Импортируем необходимые SVG-иконки для платформ
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.129.332.202.043.072.043.419-.101.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.029 18.88c-1.161 0-2.305-.292-3.318-.844l-3.677.964.984-3.595c-.607-1.052-.927-2.246-.926-3.468.001-3.825 3.113-6.937 6.937-6.937 1.856.001 3.598.723 4.907 2.034 1.31 1.311 2.031 3.054 2.03 4.908-.001 3.825-3.113 6.938-6.937 6.938z"/>
  </svg>
);

// Типы для подразделов меню
type MenuSubsection = {
  id: string;
  label: string;
  icon?: React.ComponentType<any> | (() => JSX.Element);
};

// Типы для разделов меню
type MenuItem = {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  subsections: MenuSubsection[];
};

// Типы для групп меню
type MenuGroup = {
  title: string;
  items: MenuItem[];
};

interface SidebarMenuProps {
  isOpen: boolean;
  onClose?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('menu');
  const menuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Используем хук для получения информации о размере и ориентации
  const responsive = useResponsive();
  const { isMobile, isLandscape } = responsive;
  
  // Компактный режим для горизонтальной ориентации на мобильных
  const isCompactMode = isMobile && isLandscape;
  
  // Состояние для отслеживания открытых разделов
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  
  // Определение активного раздела и подраздела
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/personal/settings')) return 'settings';
    if (path.includes('/personal/platforms')) return 'platforms';
    if (path.includes('/personal/analytics')) return 'analytics';
    return 'platforms'; // По умолчанию
  };
  
  const getActiveSubsection = () => {
    const path = location.pathname;
    const parts = path.split('/');
    if (parts.length >= 4) {
      return parts[3]; // Третий сегмент URL, например "whatsapp", "application"
    }
    return '';
  };
  
  const activeSection = getActiveSection();
  const activeSubsection = getActiveSubsection();
  
  // Проверяем, находимся ли мы уже в /personal
  
  // Инициализация expandedSections на основе активного раздела
  useEffect(() => {
    setExpandedSections(prev => ({
      ...prev,
      [activeSection]: true
    }));
  }, [activeSection]);
  
  // Эффект для прокрутки к активному элементу в меню
  useEffect(() => {
    if (isOpen && menuRef.current) {
      // Находим активный элемент в меню
      const activeElement = menuRef.current.querySelector(`.${styles.active}`) as HTMLElement | null;
      if (activeElement) {
        // Плавно прокручиваем к активному элементу с небольшим отступом сверху
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isOpen, activeSection, activeSubsection]);
  
  // Эффект для блокировки прокрутки при открытом сайдбаре
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // Обновленный эффект для обработки клика вне меню - работает для всех типов устройств и ориентаций
  useEffect(() => {
    // Обрабатываем только если меню открыто
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      // Проверяем, был ли клик вне сайдбара
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        // Вызываем функцию закрытия
        onClose?.();
      }
    };
    
    // Устанавливаем обработчик на всем документе
    document.addEventListener('mousedown', handleClickOutside);
    
    // Убираем обработчик при размонтировании компонента
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  // Функция для переключения состояния раздела (раскрыт/свёрнут)
  const toggleSection = (sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Функция для создания URL подраздела с учетом текущего пути
  const getSubsectionUrl = (section: string, subsection: string) => {
    // Всегда используем абсолютный путь для избежания проблем с относительными URL
    return `/personal/${section}/${subsection}`;
  };
  
  // Функция для обработки клика по подразделу
  const handleSubsectionClick = (url: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    // Используем navigate для SPA-навигации
    navigate(url);
    
    // Закрываем сайдбар после выбора подраздела, независимо от устройства или ориентации
    if (onClose) {
      setTimeout(() => onClose(), 150);
    }
  };
  
  // Структура бокового меню
  const menuGroups: MenuGroup[] = [
    {
      title: t('main_sections'),
      items: [        
        { 
          id: 'platforms',
          label: t('platforms'), 
          icon: MessageCircle,
          subsections: [
            { id: 'whatsapp', label: t('whatsapp'), icon: WhatsAppIcon },
            // { id: 'broadcasts', label: t('broadcasts'), icon: SendIcon }, // Временно отключено
            { id: 'orders', label: t('orders'), icon: ShoppingCart },
            { id: 'clients', label: t('clients'), icon: Users }
          ]
        },
        { 
          id: 'analytics',
          label: t('analytics'), 
          icon: BarChart2,
          subsections: [
            { id: 'dashboard', label: t('dashboard'), icon: LayoutDashboard },
            // Удален пункт sales
            { id: 'customer', label: t('customer'), icon: Users },
            // Удален пункт marketing
            { id: 'marketing-expenses', label: t('marketing_expenses'), icon: BarChart },
            { id: 'feedback', label: t('feedback'), icon: MessageSquare },
            { id: 'funnel', label: t('funnel'), icon: Filter }
          ]
        },
        { 
          id: 'settings',
          label: t('settings'), 
          icon: Settings,
          subsections: [
            { id: 'application', label: t('application') },
            { id: 'platform', label: t('platform') },
            { id: 'promotions', label: t('promotions') }
          ]
        }
      ]
    }
  ];
  
  return (
    <>
      {/* Затемнение фона при открытом сайдбаре */}
      {isOpen && (
        <div 
          className={styles.overlay}
          onClick={onClose}
        />
      )}
      
      {/* Боковое меню с адаптивными стилями */}
      <div 
        ref={sidebarRef}
        className={`${styles.sidebar} ${isOpen ? styles.open : ''} ${isCompactMode ? styles.sidebarCompact : ''}`}
      >
        <div className={styles.sidebarContainer}>
          {/* Заголовок с кнопкой закрытия */}
          <div className={styles.header}>
            <h2 className={styles.title}>{t('menu')}</h2>
            <button 
              onClick={onClose}
              className={styles.closeButton}
              aria-label={t('close')}
            >
              <X className={styles.closeIcon} />
            </button>
          </div>
          
          {/* Содержимое меню с адаптивной прокруткой */}
          <div 
            ref={menuRef}
            className={`${styles.menuContent} ${isCompactMode ? styles.compact : ''}`}
          >
            {menuGroups.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.group}>
                {/* Заголовок группы */}
                <h3 className={styles.groupTitle}>
                  {group.title}
                </h3>
                
                {/* Пункты меню */}
                <div className={styles.menuItems}>
                  {group.items.map((item) => {
                    const isExpanded = expandedSections[item.id] || false;
                    const isActive = activeSection === item.id;
                    const Icon = item.icon;
                    
                    return (
                      <div key={item.id} className={styles.menuItem}>
                        {/* Основной элемент меню (секция) */}
                        <button
                          onClick={(e) => toggleSection(item.id, e)}
                          className={`${styles.menuItemButton} ${isActive ? styles.active : ''}`}
                        >
                          <div className={styles.menuItemContent}>
                            <Icon className={styles.menuItemIcon} />
                            <span>{item.label}</span>
                          </div>
                          {isExpanded ? 
                            <ChevronUp className={styles.menuItemChevron} /> : 
                            <ChevronDown className={styles.menuItemChevron} />
                          }
                        </button>
                        
                        {/* Подразделы меню с адаптивными стилями */}
                        {isExpanded && (
                          <div className={styles.subMenuItems}>
                            {item.subsections.map((subsection) => {
                              const url = getSubsectionUrl(item.id, subsection.id);
                              const isSubsectionActive = activeSubsection === subsection.id;
                              const SubsectionIcon = subsection.icon;
                              
                              return (
                                <button
                                  key={subsection.id}
                                  onClick={(e) => handleSubsectionClick(url, e)}
                                  className={`${styles.subMenuItem} ${isSubsectionActive ? styles.active : ''}`}
                                >
                                  {SubsectionIcon && (
                                    <div className={styles.subMenuItemIcon}>
                                      <SubsectionIcon />
                                    </div>
                                  )}
                                  <span>{subsection.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SidebarMenu;